import { useEffect, useState } from 'react'
import { Box, Text, Link, VStack, Divider, SimpleGrid, Spinner } from '@chakra-ui/react'
import { containerWidth } from 'styles'
import Card from 'components/Card'
import { FaFileAlt, FaExternalLinkAlt } from 'react-icons/fa'
import { API_URL } from 'constants'
import { getDocumentCategories, getDocumentsByCategory } from 'services/documents'

const DocumentLink = ({ doc }) => {
  const href = doc.external_url
    ? doc.external_url
    : `${API_URL}/s3/${encodeURIComponent(doc.file_key)}`

  return (
    <Link
      href={href}
      isExternal
      display="flex"
      alignItems="center"
      gap={2}
      py={2}
      px={3}
      borderRadius="md"
      _hover={{ bg: 'gray.100', textDecoration: 'none' }}
      color="green.600"
      fontWeight="medium"
    >
      <FaFileAlt />
      {doc.name}
      <FaExternalLinkAlt size={12} style={{ marginLeft: 'auto', opacity: 0.5 }} />
    </Link>
  )
}

const Documents = () => {
  const [categories, setCategories] = useState([])
  const [documents, setDocuments] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      try {
        const catRes = await getDocumentCategories()
        if (catRes.data) {
          setCategories(catRes.data)
          const docsMap = {}
          for (const cat of catRes.data) {
            const docsRes = await getDocumentsByCategory(cat._id)
            docsMap[cat._id] = docsRes.data || []
          }
          setDocuments(docsMap)
        }
      } catch (err) {
        console.error('Failed to load documents:', err)
      }
      setLoading(false)
    }
    loadData()
  }, [])

  if (loading) {
    return (
      <Box width={containerWidth} mx="auto" py={10} textAlign="center">
        <Spinner size="xl" />
      </Box>
    )
  }

  return (
    <Box width={containerWidth} mx='auto' pb={10}>
      <Text fontSize='2xl' fontWeight='bold' mb={2}>
        Talent Information
      </Text>
      <Box mb={6} color="gray.600">
        <Text mb={2}>
          *Please return the signed paperwork below to{' '}
          <Link href="mailto:admin@palomamodelandtalent.com" color="green.600">
            admin@palomamodelandtalent.com
          </Link>
          .
        </Text>
        <Text mb={2}>
          *If you have any questions or concerns about the new contract and additional paperwork, please consult with your primary agent.
        </Text>
        <Text>
          *If talent is a member of SAG-AFTRA, please email{' '}
          <Link href="mailto:admin@palomamodelandtalent.com" color="green.600">
            admin@palomamodelandtalent.com
          </Link>
          {' '}or your primary agent for a copy of this contract for SAG-AFTRA members.
        </Text>
      </Box>

      <VStack spacing={8} align="stretch">
        {categories.map(category => (
          <Card key={category._id} title={category.name}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
              {(documents[category._id] || []).map(doc => (
                <DocumentLink key={doc._id} doc={doc} />
              ))}
              {(documents[category._id] || []).length === 0 && (
                <Text color="gray.500" fontStyle="italic">
                  No documents available
                </Text>
              )}
            </SimpleGrid>
          </Card>
        ))}

        {categories.length === 0 && (
          <Text color="gray.500" textAlign="center">
            No documents have been uploaded yet.
          </Text>
        )}

        {/* Agency Contact Information - Static Section */}
        <Card title="Agency Contact Information" containerProps={{ id: 'agency-contacts' }}>
          <Box mb={4}>
            <Text fontWeight="bold" mb={3}>Agents</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <Text fontWeight="medium">Paloma Jackson</Text>
                <Text fontSize="sm" color="gray.600">Owner & Print Agent</Text>
                <Link href="mailto:paloma@palomamodelandtalent.com" color="green.600" fontSize="sm">
                  paloma@palomamodelandtalent.com
                </Link>
              </Box>
              <Box>
                <Text fontWeight="medium">Alysa Brennan</Text>
                <Text fontSize="sm" color="gray.600">Owner & Theatrical Agent - Ages 14 and up</Text>
                <Link href="mailto:alysa@palomamodelandtalent.com" color="green.600" fontSize="sm">
                  alysa@palomamodelandtalent.com
                </Link>
              </Box>
              <Box>
                <Text fontWeight="medium">Melinda Gordon</Text>
                <Text fontSize="sm" color="gray.600">Theatrical Agent (and all on AA) - Ages 4-13 and LAC Commercial Agent ages 4-8</Text>
                <Link href="mailto:melinda@palomamodelandtalent.com" color="green.600" fontSize="sm">
                  melinda@palomamodelandtalent.com
                </Link>
              </Box>
              <Box>
                <Text fontWeight="medium">Kristi Koopmans</Text>
                <Text fontSize="sm" color="gray.600">Commercial Agent - Ages 9 and up (LAC only)</Text>
                <Link href="mailto:kristi@palomamodelandtalent.com" color="green.600" fontSize="sm">
                  kristi@palomamodelandtalent.com
                </Link>
              </Box>
              <Box>
                <Text fontWeight="medium">Julie Cruz</Text>
                <Text fontSize="sm" color="gray.600">Baby/Toddler Agent - Ages 0-3 (LAC and AA)</Text>
                <Link href="mailto:julie@palomamodelandtalent.com" color="green.600" fontSize="sm">
                  julie@palomamodelandtalent.com
                </Link>
              </Box>
              <Box>
                <Text fontWeight="medium">Renee Perkins</Text>
                <Text fontSize="sm" color="gray.600">Inclusive Division Agent</Text>
                <Link href="mailto:renee@palomamodelandtalent.com" color="green.600" fontSize="sm">
                  renee@palomamodelandtalent.com
                </Link>
              </Box>
              <Box>
                <Text fontWeight="medium">Geri Sterling</Text>
                <Text fontSize="sm" color="gray.600">Document Delivery (W9, CA & WP), Represented Talent general inquiries, and all CF inquiries</Text>
                <Link href="mailto:admin@palomamodelandtalent.com" color="green.600" fontSize="sm">
                  admin@palomamodelandtalent.com
                </Link>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider my={4} />

          <Box>
            <Text fontWeight="bold" mb={3}>General Contacts</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <Text fontWeight="medium">General Assistant</Text>
                <Link href="mailto:assistant@palomamodelandtalent.com" color="green.600" fontSize="sm">
                  assistant@palomamodelandtalent.com
                </Link>
              </Box>
              <Box>
                <Text fontWeight="medium">General Info</Text>
                <Link href="mailto:info@palomamodelandtalent.com" color="green.600" fontSize="sm">
                  info@palomamodelandtalent.com
                </Link>
              </Box>
              <Box>
                <Text fontWeight="medium">Photo Review</Text>
                <Text fontSize="sm" color="gray.600">(see Photo Information before emailing)</Text>
                <Link href="mailto:photo.review@palomamodelandtalent.com" color="green.600" fontSize="sm">
                  photo.review@palomamodelandtalent.com
                </Link>
              </Box>
              <Box>
                <Text fontWeight="medium">Accounting</Text>
                <Link href="mailto:accounting@palomamodelandtalent.com" color="green.600" fontSize="sm">
                  accounting@palomamodelandtalent.com
                </Link>
              </Box>
              <Box>
                <Text fontWeight="medium">Website Technical Help</Text>
                <Link href="mailto:help@palomamodelandtalent.com" color="green.600" fontSize="sm">
                  help@palomamodelandtalent.com
                </Link>
              </Box>
              <Box>
                <Text fontWeight="medium">Submissions Assistance</Text>
                <Link href="mailto:submissions@palomamodelandtalent.com" color="green.600" fontSize="sm">
                  submissions@palomamodelandtalent.com
                </Link>
              </Box>
            </SimpleGrid>
          </Box>
        </Card>
      </VStack>
    </Box>
  )
}

export default Documents
